import { NavLink, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import {
  LayoutDashboard,
  Users,
  CandlestickChart,
  BookOpen,
  Tag,
  Share2,
  TrendingUp,
  CreditCard,
  Settings,
  LogOut,
  ArrowLeft,
  BrainCircuit,
  History,
  FileText,
  ShieldAlert,
  Rocket,
  BarChart2,
  FlaskConical,
} from 'lucide-react';
import AdminMobileNav, { type AdminMobileNavItem } from './AdminMobileNav';
import { BRAND } from '../../config/brand';
import { getLaunchMode } from '../../services/launchMode.service';

interface SidebarItem {
  icon: typeof LayoutDashboard;
  label: string;
  href: string;
  badge?: string;
}

interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

export default function AdminLayout() {
  const { user, logout } = useAuthStore();
  const { t } = useTranslation('admin');
  const location = useLocation();
  const [launchModeOn, setLaunchModeOn] = useState(false);

  useEffect(() => {
    getLaunchMode()
      .then((state) => setLaunchModeOn(state.enabled))
      .catch(() => {
        // Silently ignore — badge is non-critical
      });
  }, [location.pathname]);

  const sidebarSections: SidebarSection[] = [
    {
      title: t('layout.sectionTrading'),
      items: [
        { icon: LayoutDashboard, label: t('layout.nav.dashboard'), href: '/admin' },
        { icon: Users, label: t('layout.nav.traders'), href: '/admin/traders' },
        { icon: CandlestickChart, label: t('layout.nav.signals'), href: '/admin/signals' },
        { icon: BrainCircuit, label: t('layout.nav.analysisConfigs'), href: '/admin/analysis-configs' },
        { icon: History, label: t('layout.nav.analysisRuns'), href: '/admin/analysis-runs' },
        { icon: FileText, label: t('layout.nav.prompts'), href: '/admin/prompts' },
        { icon: BarChart2, label: t('layout.nav.modelBenchmark'), href: '/admin/model-benchmark' },
        { icon: FlaskConical, label: t('layout.nav.backtest'), href: '/admin/backtest' },
        { icon: BookOpen, label: t('layout.nav.journal'), href: '/admin/journal' },
      ],
    },
    {
      title: t('layout.sectionGrowth'),
      items: [
        { icon: Tag, label: t('layout.nav.coupons'), href: '/admin/coupons' },
        { icon: Share2, label: t('layout.nav.referrals'), href: '/admin/referrals' },
        { icon: TrendingUp, label: t('layout.nav.insights'), href: '/admin/insights' },
      ],
    },
    {
      title: t('layout.sectionSystem'),
      items: [
        { icon: CreditCard, label: t('layout.nav.plans'), href: '/admin/plans' },
        { icon: CreditCard, label: t('layout.nav.subscriptions', 'Assinaturas'), href: '/admin/subscriptions' },
        { icon: Settings, label: t('layout.nav.config'), href: '/admin/config' },
        { icon: Rocket, label: t('layout.nav.launchMode'), href: '/admin/launch-mode', badge: launchModeOn ? 'ON' : undefined },
      ],
    },
  ];

  // Flat list reused by the mobile bottom nav (preserves section order).
  const mobileNavItems: AdminMobileNavItem[] = sidebarSections.flatMap(
    (section) => section.items,
  );

  const isActive = (href: string) => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="flex h-screen bg-bg-primary">
      {/* Sidebar — desktop only; mobile uses bottom nav */}
      <aside className="hidden md:flex w-[240px] flex-col bg-bg-secondary border-r border-border-subtle">
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-subtle">
          <div className="w-8 h-8 rounded-lg bg-accent-primary flex items-center justify-center">
            <span className="text-white font-bold text-sm">PM</span>
          </div>
          <span className="font-semibold text-text-primary">{BRAND.name}</span>
        </div>

        {/* Back to App + Admin Mode indicator */}
        <div className="px-3 pt-3 pb-2 border-b border-border-subtle">
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
          >
            <ArrowLeft aria-hidden="true" className="w-4 h-4 shrink-0" />
            {t('layout.backToApp')}
          </NavLink>
          <div className="flex items-center gap-2 px-3 py-2 mt-1">
            <ShieldAlert aria-hidden="true" className="w-3.5 h-3.5 text-warning shrink-0" />
            <span className="text-xs font-semibold uppercase tracking-wider text-warning">
              {t('layout.adminMode')}
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {sidebarSections.map((section) => (
            <div key={section.title} className="mb-4">
              <h3 className="px-5 py-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                {section.title}
              </h3>
              <ul className="space-y-1 px-3">
                {section.items.map((item) => (
                  <li key={item.href}>
                    <NavLink
                      to={item.href}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item.href)
                          ? 'bg-accent-primary/10 text-accent-primary'
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-primary/20 text-accent-primary leading-none">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-border-subtle">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-accent-primary/20 flex items-center justify-center">
              <span className="text-accent-primary font-semibold">
                {user?.name?.charAt(0).toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">
                {user?.name || 'Admin'}
              </p>
              <p className="text-xs text-text-muted truncate">
                {user?.email}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-loss hover:bg-loss/10 rounded-lg transition-colors"
          >
            <LogOut aria-hidden="true" className="w-4 h-4" />
            {t('layout.logout')}
          </button>
        </div>
      </aside>

      {/* Main Content (extra bottom padding on mobile to clear bottom nav) */}
      <main className="flex-1 overflow-auto bg-bg-primary pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom navigation */}
      <AdminMobileNav items={mobileNavItems} />
    </div>
  );
}
